/*
 * Copyright (c) 2025. BrightNight. All rights reserved.
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential.
 */
import {
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  type ToggleButtonGroupProps,
  type ToggleButtonProps,
  Typography,
} from '@mui/material';
import { useMemo } from 'react';
import { useFieldContext } from './formContext';

type ToggleOption<K extends string> = { key: K; label: string } & Partial<ToggleButtonProps>;

type Props<K extends string> = {
  options: Array<K> | Record<K, string> | Array<ToggleOption<K>>;
  exclusive?: boolean;
  size?: ToggleButtonGroupProps['size'] | 'tiny' | 'xs';
  color?: ToggleButtonGroupProps['color'];
  disabled?: boolean;
  onChange?: (value: K | K[] | null) => void;
  label?: string;
  customLabel?: React.ReactNode;
};

export function FormToggleButtonGroup<K extends string>({
  options,
  exclusive = false,
  size,
  color,
  disabled = false,
  label,
  customLabel,
  onChange,
}: Readonly<Props<K>>) {
  const field = useFieldContext<K | K[] | null>();
  const normalizedOptions = useMemo<Array<ToggleOption<K>>>(() => {
    if (Array.isArray(options)) {
      return options.map((option) =>
        typeof option === 'string' ? { key: option, label: option } : option,
      );
    }

    return Object.entries(options).map(([key, optionLabel]) => ({
      key: key as K,
      label: optionLabel as string,
    }));
  }, [options]);
  const selectedValues =
    field.state.value ?? (exclusive || normalizedOptions.length === 1 ? '' : []);

  const handleChange = (
    _: React.MouseEvent<HTMLElement>,
    newValue: ToggleButtonGroupProps['value'],
  ) => {
    let updated: K | K[] | null;
    if (normalizedOptions.length === 1) {
      updated = field.state.value ? null : normalizedOptions[0].key;
    } else if (exclusive) {
      updated = (newValue as K | null) ?? (field.state.value as K | null);
    } else if (Array.isArray(newValue)) {
      updated = newValue as K[];
    } else if (newValue != null) {
      updated = [newValue as K];
    } else {
      updated = [];
    }
    field.handleChange(updated);
    onChange?.(updated);
  };

  const groupSize = size === 'tiny' || size === 'xs' ? 'small' : size;
  let labelFontSize = 14;
  if (size === 'tiny') labelFontSize = 11;
  else if (size === 'xs') labelFontSize = 12;
  let buttonSx: object | undefined;
  if (size === 'tiny') buttonSx = { fontSize: 11, lineHeight: 1, py: 0.3, px: 0.8, minHeight: 25 };
  else if (size === 'xs')
    buttonSx = { fontSize: 12, lineHeight: 1, py: 0.45, px: 1, minHeight: 30 };

  return (
    <Stack direction="row" sx={{ alignItems: 'center', justifyContent: 'center' }}>
      {customLabel ??
        (label ? <Typography sx={{ fontSize: labelFontSize, mr: 1 }}>{label}</Typography> : null)}
      {normalizedOptions.length === 1 ? (
        normalizedOptions.map(({ key, label: optionLabel, ...rest }) => (
          <ToggleButton
            key={key}
            value={key}
            selected={field.state.value === key}
            size={groupSize}
            color={color}
            disabled={disabled}
            onChange={handleChange}
            data-testid={field.name.split('.').at(-1)}
            sx={buttonSx}
            {...rest}
          >
            {optionLabel}
          </ToggleButton>
        ))
      ) : (
        <ToggleButtonGroup
          value={selectedValues}
          exclusive={exclusive}
          size={groupSize}
          color={color}
          disabled={disabled}
          onChange={handleChange}
          data-testid={field.name.split('.').at(-1)}
        >
          {normalizedOptions.map(({ key, label: optionLabel, ...rest }) => (
            <ToggleButton key={key} value={key} sx={buttonSx} {...rest}>
              {optionLabel}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
      )}
    </Stack>
  );
}
